"use client";

import { useQuery } from "@apollo/client";
import { ChartData } from "chart.js";
import { ArrowRight, BarChart3, Layers, LineChart, UserRound } from "lucide-react";
import { Modal, ModalButtons } from "components/ui/v2";
import { gqlVendor } from "gql";
import useTranslation from "next-translate/useTranslation";
import { useState } from "react";
import { Bar } from "react-chartjs-2";
import { EUserSeniority, IUser } from "types/User.types";
import { ISeniorityStats } from "types/Vendor.types";
import { userSeniorityDescription } from "utils/string";
import { HomeInfoBannerV2 } from "./HomeInfoBannerV2";
import { HomeLoadingStateV2 } from "./HomeLoadingStateV2";
import { HomeMetricCardV2 } from "./HomeMetricCardV2";
import { HomeSectionCardV2 } from "./HomeSectionCardV2";
import {
  SENIORITY_DETAILS_FORM_ID,
  SeniorityDetailsModalContentV2,
} from "./SeniorityDetailsModalContentV2";

export function DashboardSeniorityPanelV2() {
  const { t, lang } = useTranslation("common");
  const [chartData, setChartData] = useState<ChartData<"bar">>();
  const [detailsModal, setDetailsModal] = useState<{
    users: IUser[];
    seniority: EUserSeniority;
    title: string;
  } | null>(null);
  const { data } = useQuery<{ seniorityStats: ISeniorityStats[] }>(
    gqlVendor.queries.GET_VENDOR_SENIORITY_STATS,
    {
      onCompleted: ({ seniorityStats }) => {
        const labels = seniorityStats.map(({ seniority }) =>
          userSeniorityDescription(seniority, lang)
        );
        setChartData({
          labels,
          datasets: [
            {
              data: seniorityStats.map((stat) => stat.quantity),
              backgroundColor: ["#f38b4a", "#56d798", "#ff8397", "#6970d5"],
              hoverBackgroundColor: [
                "#f38b4a",
                "#56d798",
                "#ff8397",
                "#6970d5"
              ],
              borderWidth: 0,
              borderRadius: 10
            }
          ]
        });
      }
    }
  );

  const totalUsers =
    data?.seniorityStats.reduce((acc, stat) => acc + stat.quantity, 0) || 0;
  const mostCommon =
    data && data.seniorityStats.length > 0
      ? userSeniorityDescription(
          [...data.seniorityStats].sort((a, b) => b.quantity - a.quantity)[0]
            ?.seniority,
          lang
        )
      : t("N/A");
  const definedLevels =
    data?.seniorityStats.filter((stat) => stat.quantity > 0).length || 0;
  const maxQuantity = Math.max(
    ...(data?.seniorityStats.map((stat) => stat.quantity) || [0])
  );

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: t("Estadísticas de Seniority Informado")
      }
    }
  };

  const handleOpenDetails = (stat: ISeniorityStats) => {
    setDetailsModal({
      users: stat.users || [],
      seniority: stat.seniority || EUserSeniority.UNDEFINED,
      title: userSeniorityDescription(stat.seniority, lang)
    });
  };

  return (
    <>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <HomeMetricCardV2
          title={t("Total Usuarios")}
          value={totalUsers}
          filled
          loading={!data}
          icon={<Layers className="h-4 w-4 text-orange-500" />}
        />
        <HomeMetricCardV2
          title={t("Niveles definidos")}
          value={definedLevels}
          filled
          loading={!data}
          icon={<BarChart3 className="h-4 w-4 text-blue-500" />}
        />
        <HomeMetricCardV2
          title={t("Mayor cantidad")}
          value={maxQuantity}
          filled
          loading={!data}
          icon={<LineChart className="h-4 w-4 text-purple-500" />}
        />
        <HomeSectionCardV2
          title={t("Seniority más común")}
          surface="default"
        >
          <p className="text-lg font-bold text-slate-900 dark:text-zinc-100">
            {data ? mostCommon : <HomeLoadingStateV2 inline compact />}
          </p>
        </HomeSectionCardV2>
      </div>

      <HomeInfoBannerV2>
        <p>
          {t(
            "Estas estadísticas muestran la cantidad de usuarios que han informado su seniority en la plataforma"
          )}
        </p>
      </HomeInfoBannerV2>

      <div className="mt-6">
        {!chartData ? (
          <HomeSectionCardV2>
            <div className="flex flex-col items-center justify-center py-16">
              <HomeLoadingStateV2 />
              <p className="mt-4 text-sm text-slate-600 dark:text-zinc-400">
                {t("Cargando estadísticas de seniority")}
              </p>
            </div>
          </HomeSectionCardV2>
        ) : (
          <div className="space-y-4">
            <HomeSectionCardV2 title={t("Distribución por nivel")}>
              <Bar options={options} data={chartData} />
            </HomeSectionCardV2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data?.seniorityStats
                .filter((q) => q.quantity > 0)
                .map((stat) => (
                  <button
                    key={stat.seniority}
                    type="button"
                    onClick={() => handleOpenDetails(stat)}
                    className="w-full text-left"
                  >
                    <HomeSectionCardV2
                      surface="interactive"
                      title={userSeniorityDescription(stat.seniority, lang)}
                      description={`${stat.quantity} ${t("Usuarios")}`}
                      actions={
                        <span className="flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                          {t("Ver detalles")}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-indigo-100 p-3 dark:bg-indigo-900/30">
                          <UserRound className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <span className="text-2xl font-bold text-slate-900 dark:text-zinc-100">
                          {stat.quantity}
                        </span>
                      </div>
                    </HomeSectionCardV2>
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      <Modal
        open={Boolean(detailsModal)}
        onClose={() => setDetailsModal(null)}
        title={detailsModal?.title}
        size="lg"
        footer={
          <ModalButtons
            compact
            onCancel={() => setDetailsModal(null)}
            cancelLabel={t("Cerrar")}
            submitLabel={t("Solicitar actualización a líderes")}
            submitButtonProps={{
              form: SENIORITY_DETAILS_FORM_ID,
              type: "submit",
            }}
          />
        }
      >
        {detailsModal && (
          <SeniorityDetailsModalContentV2
            users={detailsModal.users}
            seniority={detailsModal.seniority}
            onClose={() => setDetailsModal(null)}
            hideHeader
            hideFooter
          />
        )}
      </Modal>
    </>
  );
}
